import { AsYouTypeFormatter, PhoneNumber, PhoneNumberUtil } from 'google-libphonenumber';
import { useEffect, useState } from 'react';
import { FieldError } from 'react-hook-form';
import { Platform, StyleSheet, TextInput, View } from 'react-native';

import { ENV } from '@/constants/config';
import { useColor } from '@/hooks/useColor';
import { i18n } from '@/utils/i18n';

import Text from '../Text';
import CustomCountryPicker, { CountryCode } from './CustomCountryPicker';

interface PhoneInputProps {
  initialValue?: string;
  label?: string;
  placeholder?: string;
  variant?: 'default' | 'rounded_with_shadow' | 'small_border_radius';
  showError?: boolean;
  error?: FieldError;
  shouldClearField?: boolean;
  onNumberChange?: (number?: PhoneNumber) => void;
}

const PhoneInput = ({
  label,
  initialValue,
  placeholder = i18n.t('sign_in.individual.phone_sign_in.placeholder'),
  variant = 'default',
  error,
  shouldClearField,
  onNumberChange
}: PhoneInputProps) => {
  const colors = useColor();
  const [formattedNumber, setFormattedNumber] = useState<string>('');
  const [countryCode, setCountryCode] = useState<CountryCode>('US');

  const onChangeTextPhone = (t: string) => {
    const formatter = new AsYouTypeFormatter(countryCode);

    const inputNumber = t.replace(/\D/g, '');
    formatter.clear();
    let formattedNumber = '';

    for (let i = 0; i < inputNumber.length; i++) {
      formattedNumber = formatter.inputDigit(inputNumber[i]);
    }

    setFormattedNumber(formattedNumber);

    let validNumber = false;
    let number: PhoneNumber | undefined = undefined;
    try {
      const numberUtil = PhoneNumberUtil.getInstance();
      number = numberUtil.parse(formattedNumber, countryCode);
      validNumber = numberUtil.isValidNumber(number);
    } catch {
      validNumber = false;
    }

    onNumberChange?.(validNumber ? number : undefined);
  };

  useEffect(() => {
    if (formattedNumber !== '') {
      onChangeTextPhone(formattedNumber);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryCode]);

  useEffect(() => {
    if (initialValue) setFormattedNumber(initialValue);
  }, [initialValue]);

  useEffect(() => {
    if (shouldClearField) setFormattedNumber('');
  }, [shouldClearField]);

  return (
    <View>
      {label && (
        <Text fontWeight="700" style={styles.label}>
          {label}
        </Text>
      )}
      <View style={styles.inputRow}>
        {ENV !== 'production' ? (
          <CustomCountryPicker setCountryCode={(country) => setCountryCode(country)} variant={variant} />
        ) : (
          <View style={[styles.pickerButton, styles.countyContainer, { height: variant === 'default' ? 52 : 40 }]}>
            <Text style={styles.flagText}>🇺🇸 </Text>
            <Text style={styles.codeText}> +1</Text>
          </View>
        )}
        <TextInput
          style={[
            getInputStyle(variant),
            { color: colors.titleText },
            styles.placeholderText,
            Platform.OS === 'web' && ({ outlineStyle: 'none' } as any)
          ]}
          onChangeText={onChangeTextPhone}
          placeholder={placeholder}
          placeholderTextColor={colors.placeholderText}
          textContentType="telephoneNumber"
          keyboardType="number-pad"
          value={formattedNumber}
          clearTextOnFocus
          onFocus={() => {
            setFormattedNumber('');
            onChangeTextPhone('');
          }}
          maxLength={15}
        />
      </View>
      {!!error?.message && <Text style={styles.errorText}>{error.message}</Text>}
    </View>
  );
};

export default PhoneInput;

const getInputStyle = (variant: string) => {
  switch (variant) {
    case 'rounded_with_shadow':
      return styles.roundedWithShadow;
    case 'small_border_radius':
      return styles.smallBorderRadius;
    default:
      return styles.default;
  }
};

const styles = StyleSheet.create({
  default: {
    padding: 16,
    borderWidth: 1,
    borderRadius: 8,
    width: '100%',
    flex: 1
  },

  roundedWithShadow: {
    height: 38,
    backgroundColor: 'white',
    justifyContent: 'center',
    paddingHorizontal: 16,
    borderRadius: 50,
    shadowOffset: {
      width: 0,
      height: 3
    },
    shadowRadius: 5,
    shadowColor: 'black',
    shadowOpacity: 0.12,
    elevation: 5,
    width: '100%',
    flex: 1
  },

  smallBorderRadius: {
    height: 38,
    backgroundColor: 'white',
    justifyContent: 'center',
    paddingHorizontal: 16,
    borderRadius: 5,
    shadowOffset: {
      width: 0,
      height: 3
    },
    shadowRadius: 5,
    shadowColor: 'black',
    shadowOpacity: 0.12,
    elevation: 5,
    width: '100%',
    flex: 1
  },

  inputRow: {
    flexDirection: 'row',
    alignItems: 'center'
  },

  label: {
    color: 'black',
    marginBottom: 10
  },

  pickerButton: {
    backgroundColor: 'white',
    borderColor: '#000',
    borderRadius: 8,
    borderWidth: 1,
    paddingHorizontal: 5,
    marginRight: 16,
    minWidth: 80,
    justifyContent: 'center',
    alignItems: 'center'
  },

  placeholderText: {
    fontSize: 16
  },

  countyContainer: {
    flexDirection: 'row'
  },

  flagText: {
    fontSize: 30
  },

  codeText: {
    fontSize: 18
  },

  errorText: {
    marginTop: 10,
    fontSize: 16,
    color: 'red'
  }
});
